import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Code, Copy, CheckCircle, Book, Zap, Key, Shield, Terminal, ArrowLeft } from 'lucide-react';
import './Documentation.css';

const Documentation: React.FC = () => {
  const location = useLocation();
  const isPublicDocs = location.pathname === '/docs';
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const apiEndpoint = 'https://ai.riko.my';

  return (
    <div className="documentation-page">
      {/* Back Button for Public Docs */}
      {isPublicDocs && (
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              color: 'var(--color-text-secondary)', 
              textDecoration: 'none', 
              fontSize: '0.9rem',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1>
            <Book size={32} />
            API Documentation
          </h1>
          <p>Complete guide to integrate Flazu AI API into your applications</p>
        </div>
      </div>

      {/* Quick Start */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Zap size={24} />
            Quick Start
          </h2>
        </div>
        <div className="doc-section">
          <p>Get started with Flazu AI API in minutes. Simple REST API - no special libraries required!</p>
          
          <div className="info-box info-box-primary">
            <strong>Base URL:</strong> <code>{apiEndpoint}</code>
          </div>

          <div className="info-box info-box-success">
            <strong>✨ No Dependencies Required!</strong>
            <br/>
            This API uses standard HTTP/JSON. Use any HTTP client in any programming language - no special libraries needed!
          </div>

          <h3>1. Get Your API Key</h3>
          <ul>
            <li>Purchase a plan and receive a redeem code</li>
            <li>Register an account with your redeem code</li>
            <li>Your API key will be automatically activated</li>
          </ul>

          <h3>2. Make Your First Request</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">
                <Terminal size={16} />
                cURL
              </span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`curl ${apiEndpoint}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "model": "gpt-4",
    "messages": [
      {"role": "user", "content": "Hello!"}
    ]
  }'`, 'curl-example')}
              >
                {copiedCode === 'curl-example' ? <CheckCircle size={16} /> : <Copy size={16} />}
                {copiedCode === 'curl-example' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <pre><code>{`curl ${apiEndpoint}/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "model": "gpt-4",
    "messages": [
      {"role": "user", "content": "Hello!"}
    ]
  }'`}</code></pre>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Key size={24} />
            Authentication
          </h2>
        </div>
        <div className="doc-section">
          <p>All API requests require authentication using your API key in the Authorization header:</p>
          
          <div className="code-block-container">
            <pre><code>Authorization: Bearer YOUR_API_KEY</code></pre>
          </div>

          <div className="info-box info-box-warning">
            <strong>⚠️ Keep your API key secure!</strong> Never expose it in client-side code or public repositories.
          </div>
        </div>
      </div>

      {/* API Endpoints Overview */}
      <div className="card">
        <div className="card-header">
          <h2>API Endpoints</h2>
        </div>
        <div className="doc-section">
          <table className="params-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Method</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>/v1/models</code></td>
                <td><span className="badge badge-primary">GET</span></td>
                <td>List all available AI models</td>
              </tr>
              <tr>
                <td><code>/v1/chat/completions</code></td>
                <td><span className="badge badge-success">POST</span></td>
                <td>Create chat completions (streaming supported)</td>
              </tr>
              <tr>
                <td><code>/v1/account/info</code></td>
                <td><span className="badge badge-primary">GET</span></td>
                <td>Get your account and API key info</td>
              </tr>
              <tr>
                <td><code>/v1/account/usage</code></td>
                <td><span className="badge badge-primary">GET</span></td>
                <td>Check your usage statistics</td>
              </tr>
              <tr>
                <td><code>/v1/account/ips</code></td>
                <td><span className="badge badge-primary">GET</span></td>
                <td>List your whitelisted IP addresses</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Available Models */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Code size={24} />
            Available Models
          </h2>
        </div>
        <div className="doc-section">
          <h3>🔥 Native Models (Direct Access - Fastest)</h3>
          
          <h4 style={{ marginTop: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>OpenAI GPT Series</h4>
          <div className="models-grid">
            <div className="model-card">
              <h4>gpt-5</h4>
              <p>400K context • Most powerful</p>
              <span className="badge badge-success">Latest</span>
            </div>
            <div className="model-card">
              <h4>gpt-5-mini</h4>
              <p>400K context • Fast & efficient</p>
            </div>
            <div className="model-card">
              <h4>gpt-5-nano</h4>
              <p>400K context • Ultra fast</p>
            </div>
            <div className="model-card">
              <h4>gpt-5-codex</h4>
              <p>192K context • Code specialist</p>
            </div>
            <div className="model-card">
              <h4>gpt-4.1</h4>
              <p>1M context • Extended capacity</p>
            </div>
            <div className="model-card">
              <h4>gpt-4o</h4>
              <p>128K context • Multimodal</p>
            </div>
          </div>

          <h4 style={{ marginTop: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>Anthropic Claude Series</h4>
          <div className="models-grid">
            <div className="model-card">
              <h4>claude-4.5-sonnet</h4>
              <p>1M context • Most intelligent</p>
              <span className="badge badge-success">Recommended</span>
            </div>
            <div className="model-card">
              <h4>claude-4-sonnet</h4>
              <p>1M context • Balanced</p>
            </div>
            <div className="model-card">
              <h4>claude-4.1-opus</h4>
              <p>200K context • Creative</p>
            </div>
            <div className="model-card">
              <h4>claude-4-opus</h4>
              <p>200K context • Powerful</p>
            </div>
            <div className="model-card">
              <h4>claude-3.7-sonnet</h4>
              <p>200K context • Fast</p>
            </div>
            <div className="model-card">
              <h4>claude-3.5-sonnet</h4>
              <p>200K context • Classic</p>
            </div>
            <div className="model-card">
              <h4>claude-3.5-haiku</h4>
              <p>200K context • Ultra fast</p>
            </div>
          </div>

          <h4 style={{ marginTop: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>Google Gemini Series</h4>
          <div className="models-grid">
            <div className="model-card">
              <h4>gemini-2.5-pro</h4>
              <p>1M context • Pro version</p>
            </div>
            <div className="model-card">
              <h4>gemini-2.5-flash</h4>
              <p>1M context • Fast version</p>
            </div>
          </div>

          <h4 style={{ marginTop: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>OpenAI O-Series (Reasoning)</h4>
          <div className="models-grid">
            <div className="model-card">
              <h4>o3</h4>
              <p>200K context • Advanced reasoning</p>
            </div>
            <div className="model-card">
              <h4>o4-mini</h4>
              <p>200K context • Fast reasoning</p>
            </div>
          </div>

          <h4 style={{ marginTop: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>xAI Grok Series</h4>
          <div className="models-grid">
            <div className="model-card">
              <h4>grok-4</h4>
              <p>256K context • Latest version</p>
            </div>
            <div className="model-card">
              <h4>grok-3</h4>
              <p>1M context • Large capacity</p>
            </div>
            <div className="model-card">
              <h4>grok-3-mini</h4>
              <p>131K context • Efficient</p>
            </div>
            <div className="model-card">
              <h4>grok-code</h4>
              <p>512K context • Code focused</p>
            </div>
            <div className="model-card">
              <h4>grok-fast</h4>
              <p>256K context • Speed optimized</p>
            </div>
          </div>

          <h4 style={{ marginTop: '1.5rem', color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>DeepSeek & Others</h4>
          <div className="models-grid">
            <div className="model-card">
              <h4>deepseek-r1</h4>
              <p>128K context • Reasoning model</p>
            </div>
            <div className="model-card">
              <h4>deepseek-v3.1</h4>
              <p>128K context • Latest version</p>
            </div>
            <div className="model-card">
              <h4>kimi-k2-instruct</h4>
              <p>256K context • Moonshot AI</p>
            </div>
            <div className="model-card">
              <h4>code-supernova-1-million</h4>
              <p>1M context • Code specialist</p>
            </div>
          </div>

          <div className="info-box info-box-info" style={{ marginTop: '2rem' }}>
            <strong>💡 Note:</strong> All native models above are directly connected and verified working. Contact support for additional custom models, use the format: <code>provider/model-name</code> (e.g., <code>openai/gpt-4-turbo</code>, <code>anthropic/claude-3.5-sonnet</code>)
          </div>

          <h3>List All Models</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">Endpoint</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`GET ${apiEndpoint}/v1/models`, 'models-endpoint')}
              >
                {copiedCode === 'models-endpoint' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>GET {apiEndpoint}/v1/models</code></pre>
          </div>
        </div>
      </div>

      {/* Chat Completions */}
      <div className="card">
        <div className="card-header">
          <h2>Chat Completions API</h2>
        </div>
        <div className="doc-section">
          <p>Create AI-powered chat completions with streaming support.</p>

          <h3>Endpoint</h3>
          <div className="code-block-container">
            <pre><code>POST {apiEndpoint}/v1/chat/completions</code></pre>
          </div>

          <h3>Request Body</h3>
          <table className="params-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>model</code></td>
                <td>string</td>
                <td>✅</td>
                <td>ID of the model to use</td>
              </tr>
              <tr>
                <td><code>messages</code></td>
                <td>array</td>
                <td>✅</td>
                <td>Array of message objects</td>
              </tr>
              <tr>
                <td><code>stream</code></td>
                <td>boolean</td>
                <td>❌</td>
                <td>Enable streaming responses (default: false)</td>
              </tr>
              <tr>
                <td><code>temperature</code></td>
                <td>number</td>
                <td>❌</td>
                <td>Sampling temperature 0-2 (default: 1)</td>
              </tr>
              <tr>
                <td><code>max_tokens</code></td>
                <td>number</td>
                <td>❌</td>
                <td>Maximum tokens to generate</td>
              </tr>
            </tbody>
          </table>

          <h3>Example: Python (Pure Requests)</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">Python</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`import requests

response = requests.post(
    '${apiEndpoint}/v1/chat/completions',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    json={
        'model': 'gpt-4',
        'messages': [
            {'role': 'system', 'content': 'You are a helpful assistant.'},
            {'role': 'user', 'content': 'Hello!'}
        ]
    }
)

data = response.json()
print(data['choices'][0]['message']['content'])`, 'python-example')}
              >
                {copiedCode === 'python-example' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`import requests

response = requests.post(
    '${apiEndpoint}/v1/chat/completions',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    json={
        'model': 'gpt-4',
        'messages': [
            {'role': 'system', 'content': 'You are a helpful assistant.'},
            {'role': 'user', 'content': 'Hello!'}
        ]
    }
)

data = response.json()
print(data['choices'][0]['message']['content'])`}</code></pre>
          </div>

          <h3>Example: JavaScript (Pure Fetch)</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">JavaScript</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`const response = await fetch('${apiEndpoint}/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    model: 'gpt-4',
    messages: [
      { role: 'system', content: 'You are a helpful assistant.' },
      { role: 'user', content: 'Hello!' }
    ]
  })
});

const data = await response.json();
console.log(data.choices[0].message.content);`, 'nodejs-example')}
              >
                {copiedCode === 'nodejs-example' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`const response = await fetch('${apiEndpoint}/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    model: 'gpt-4',
    messages: [
      { role: 'system', content: 'You are a helpful assistant.' },
      { role: 'user', content: 'Hello!' }
    ]
  })
});

const data = await response.json();
console.log(data.choices[0].message.content);`}</code></pre>
          </div>

          <h3>Example: Node.js (with Axios)</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">JavaScript</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`const axios = require('axios');

const response = await axios.post('${apiEndpoint}/v1/chat/completions', {
  model: 'gpt-4',
  messages: [
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: 'Hello!' }
  ]
}, {
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  }
});

console.log(response.data.choices[0].message.content);`, 'axios-example')}
              >
                {copiedCode === 'axios-example' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`const axios = require('axios');

const response = await axios.post('${apiEndpoint}/v1/chat/completions', {
  model: 'gpt-4',
  messages: [
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: 'Hello!' }
  ]
}, {
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  }
});

console.log(response.data.choices[0].message.content);`}</code></pre>
          </div>

          <h3>Streaming Example (Python)</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">Python (Streaming)</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`import requests
import json

response = requests.post(
    '${apiEndpoint}/v1/chat/completions',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    json={
        'model': 'gpt-4',
        'messages': [{'role': 'user', 'content': 'Tell me a story'}],
        'stream': True
    },
    stream=True
)

for line in response.iter_lines():
    if line:
        line = line.decode('utf-8')
        if line.startswith('data: '):
            data = line[6:]
            if data != '[DONE]':
                chunk = json.loads(data)
                content = chunk['choices'][0]['delta'].get('content', '')
                if content:
                    print(content, end='', flush=True)`, 'streaming-python')}
              >
                {copiedCode === 'streaming-python' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`import requests
import json

response = requests.post(
    '${apiEndpoint}/v1/chat/completions',
    headers={
        'Content-Type': 'application/json',
        'Authorization': 'Bearer YOUR_API_KEY'
    },
    json={
        'model': 'gpt-4',
        'messages': [{'role': 'user', 'content': 'Tell me a story'}],
        'stream': True
    },
    stream=True
)

for line in response.iter_lines():
    if line:
        line = line.decode('utf-8')
        if line.startswith('data: '):
            data = line[6:]
            if data != '[DONE]':
                chunk = json.loads(data)
                content = chunk['choices'][0]['delta'].get('content', '')
                if content:
                    print(content, end='', flush=True)`}</code></pre>
          </div>

          <h3>Example: PHP</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">PHP</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`<?php
$ch = curl_init('${apiEndpoint}/v1/chat/completions');

curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json',
    'Authorization: Bearer YOUR_API_KEY'
]);

curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'model' => 'gpt-4',
    'messages' => [
        ['role' => 'system', 'content' => 'You are a helpful assistant.'],
        ['role' => 'user', 'content' => 'Hello!']
    ]
]));

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);
echo $data['choices'][0]['message']['content'];`, 'php-example')}
              >
                {copiedCode === 'php-example' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`<?php
$ch = curl_init('${apiEndpoint}/v1/chat/completions');

curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Content-Type: application/json',
    'Authorization: Bearer YOUR_API_KEY'
]);

curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'model' => 'gpt-4',
    'messages' => [
        ['role' => 'system', 'content' => 'You are a helpful assistant.'],
        ['role' => 'user', 'content' => 'Hello!']
    ]
]));

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);
echo $data['choices'][0]['message']['content'];`}</code></pre>
          </div>

          <h3>Example: Go</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">Go</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "io"
    "net/http"
)

func main() {
    payload := map[string]interface{}{
        "model": "gpt-4",
        "messages": []map[string]string{
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": "Hello!"},
        },
    }

    jsonData, _ := json.Marshal(payload)
    req, _ := http.NewRequest("POST", "${apiEndpoint}/v1/chat/completions", bytes.NewBuffer(jsonData))
    req.Header.Set("Content-Type", "application/json")
    req.Header.Set("Authorization", "Bearer YOUR_API_KEY")

    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()

    body, _ := io.ReadAll(resp.Body)
    var result map[string]interface{}
    json.Unmarshal(body, &result)

    fmt.Println(result["choices"].([]interface{})[0].(map[string]interface{})["message"].(map[string]interface{})["content"])
}`, 'go-example')}
              >
                {copiedCode === 'go-example' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "io"
    "net/http"
)

func main() {
    payload := map[string]interface{}{
        "model": "gpt-4",
        "messages": []map[string]string{
            {"role": "system", "content": "You are a helpful assistant."},
            {"role": "user", "content": "Hello!"},
        },
    }

    jsonData, _ := json.Marshal(payload)
    req, _ := http.NewRequest("POST", "${apiEndpoint}/v1/chat/completions", bytes.NewBuffer(jsonData))
    req.Header.Set("Content-Type", "application/json")
    req.Header.Set("Authorization", "Bearer YOUR_API_KEY")

    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()

    body, _ := io.ReadAll(resp.Body)
    var result map[string]interface{}
    json.Unmarshal(body, &result)

    fmt.Println(result["choices"].([]interface{})[0].(map[string]interface{})["message"].(map[string]interface{})["content"])
}`}</code></pre>
          </div>

          <h3>Streaming Example (JavaScript)</h3>
          <div className="code-block-container">
            <div className="code-block-header">
              <span className="code-language">JavaScript (Streaming)</span>
              <button 
                className="btn-copy"
                onClick={() => copyToClipboard(`const response = await fetch('${apiEndpoint}/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    model: 'gpt-4',
    messages: [{ role: 'user', content: 'Tell me a story' }],
    stream: true
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  
  const chunk = decoder.decode(value);
  const lines = chunk.split('\\n');
  
  for (const line of lines) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6);
      if (data !== '[DONE]') {
        const parsed = JSON.parse(data);
        const content = parsed.choices[0].delta?.content;
        if (content) {
          process.stdout.write(content);
        }
      }
    }
  }
}`, 'streaming-js')}
              >
                {copiedCode === 'streaming-js' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <pre><code>{`const response = await fetch('${apiEndpoint}/v1/chat/completions', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: JSON.stringify({
    model: 'gpt-4',
    messages: [{ role: 'user', content: 'Tell me a story' }],
    stream: true
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;
  
  const chunk = decoder.decode(value);
  const lines = chunk.split('\\n');
  
  for (const line of lines) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6);
      if (data !== '[DONE]') {
        const parsed = JSON.parse(data);
        const content = parsed.choices[0].delta?.content;
        if (content) {
          process.stdout.write(content);
        }
      }
    }
  }
}`}</code></pre>
          </div>
        </div>
      </div>

      {/* Response Format */}
      <div className="card">
        <div className="card-header">
          <h2>Response Format</h2>
        </div>
        <div className="doc-section">
          <h3>Success Response</h3>
          <div className="code-block-container">
            <pre><code>{`{
  "id": "chatcmpl-abc123",
  "object": "chat.completion",
  "created": 1677652288,
  "model": "gpt-4",
  "choices": [
    {
      "index": 0,
      "message": {
        "role": "assistant",
        "content": "Hello! How can I help you today?"
      },
      "finish_reason": "stop"
    }
  ],
  "usage": {
    "prompt_tokens": 10,
    "completion_tokens": 9,
    "total_tokens": 19
  }
}`}</code></pre>
          </div>

          <h3>Streaming Response (SSE)</h3>
          <p>When <code>stream: true</code>, responses are sent as Server-Sent Events (SSE):</p>
          <div className="code-block-container">
            <pre><code>{`data: {"id":"chatcmpl-abc123","object":"chat.completion.chunk","created":1677652288,"model":"gpt-4","choices":[{"index":0,"delta":{"role":"assistant"},"finish_reason":null}]}

data: {"id":"chatcmpl-abc123","object":"chat.completion.chunk","created":1677652288,"model":"gpt-4","choices":[{"index":0,"delta":{"content":"Hello"},"finish_reason":null}]}

data: {"id":"chatcmpl-abc123","object":"chat.completion.chunk","created":1677652288,"model":"gpt-4","choices":[{"index":0,"delta":{"content":"!"},"finish_reason":null}]}

data: {"id":"chatcmpl-abc123","object":"chat.completion.chunk","created":1677652288,"model":"gpt-4","choices":[{"index":0,"delta":{},"finish_reason":"stop"}]}

data: [DONE]`}</code></pre>
          </div>
        </div>
      </div>

      {/* IP Whitelisting */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Shield size={24} />
            IP Whitelisting
          </h2>
        </div>
        <div className="doc-section">
          <p>Secure your API key by locking it to specific IP addresses.</p>

          <div className="info-box info-box-info">
            <strong>📌 How it works:</strong>
            <ul>
              <li>When you first use your API key, you must add an IP address</li>
              <li>Maximum 3 IP addresses per key</li>
              <li>PRO+ plans can manage IPs manually</li>
            </ul>
          </div>

          <h3>Add Current IP (Dashboard)</h3>
          <p>Go to <strong>API Keys</strong> page → Click <strong>"Add Current IP"</strong> button</p>

          <h3>View Your IPs</h3>
          <div className="code-block-container">
            <pre><code>GET {apiEndpoint}/v1/account/ips
Authorization: Bearer YOUR_API_KEY</code></pre>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="card">
        <div className="card-header">
          <h2>Rate Limits & Usage</h2>
        </div>
        <div className="doc-section">
          <table className="limits-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Requests/Minute</th>
                <th>Requests/Day</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="badge badge-secondary">Free</span></td>
                <td>20</td>
                <td>500</td>
              </tr>
              <tr>
                <td><span className="badge badge-primary">Pro</span></td>
                <td>60</td>
                <td>5,000</td>
              </tr>
              <tr>
                <td><span className="badge badge-warning">Enterprise</span></td>
                <td>Unlimited</td>
                <td>Unlimited</td>
              </tr>
            </tbody>
          </table>

          <h3>Check Your Usage</h3>
          <div className="code-block-container">
            <pre><code>GET {apiEndpoint}/v1/account/usage
Authorization: Bearer YOUR_API_KEY</code></pre>
          </div>
        </div>
      </div>

      {/* Error Handling */}
      <div className="card">
        <div className="card-header">
          <h2>Error Handling</h2>
        </div>
        <div className="doc-section">
          <table className="params-table">
            <thead>
              <tr>
                <th>Status Code</th>
                <th>Meaning</th>
                <th>Solution</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>401</code></td>
                <td>Unauthorized</td>
                <td>Check your API key</td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Forbidden</td>
                <td>IP not whitelisted or key expired</td>
              </tr>
              <tr>
                <td><code>429</code></td>
                <td>Rate Limit</td>
                <td>Slow down requests or upgrade plan</td>
              </tr>
              <tr>
                <td><code>500</code></td>
                <td>Server Error</td>
                <td>Contact support</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Support */}
      <div className="card">
        <div className="card-header">
          <h2>Need Help?</h2>
        </div>
        <div className="doc-section">
          <div className="support-grid">
            <div className="support-card">
              <h4>📧 Email Support</h4>
              <p>support@riko.my</p>
            </div>
            <div className="support-card">
              <h4>💬 Community</h4>
              <p>Join our Discord server</p>
            </div>
            <div className="support-card">
              <h4>📚 More Examples</h4>
              <p>Check our GitHub repository</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;